/**
 * Errors thrown by the chat transport. Framework-free (copied to next-mapit).
 */

/**
 * @typedef {number | "network" | "parse"} ChatErrorCode
 * App code from the JSON envelope `status` (851–859, see `ERROR_CODES`), or a
 * client-side transport code.
 */

/**
 * @typedef {Object} ChatApiErrorInit
 * @property {ChatErrorCode} code
 * @property {number | null} [httpStatus] HTTP status; null when no response arrived.
 * @property {string} [message] Server's localized message (may be empty).
 * @property {number | null} [retryAfterSeconds] From `Retry-After`; null when missing or unreadable.
 */

/**
 * A failed chat request. `code` is the app code, never the HTTP status.
 */
export class ChatApiError extends Error {
  /** @param {ChatApiErrorInit} init */
  constructor({
    code,
    httpStatus = null,
    message = "",
    retryAfterSeconds = null,
  }) {
    super(message);
    this.name = "ChatApiError";
    /** @type {ChatErrorCode} */
    this.code = code;
    /** @type {number | null} */
    this.httpStatus = httpStatus;
    /** @type {number | null} */
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Duck-typed by `name` so it holds across bundles/realms and transpiled
 * `extends Error` (where `instanceof` can be unreliable).
 *
 * @param {unknown} error
 * @returns {error is ChatApiError}
 */
export const isChatApiError = (error) =>
  Boolean(error) && error.name === "ChatApiError";

/**
 * The app code of an error envelope `{ status, message }` (851–863, 400 …):
 * an integer, or an all-digit string turned into one. Anything else (an HTML
 * page, `{ error }`, null, a boolean) isn't our envelope → null. Shared by
 * `streamChat` and the RTK Query endpoints, so both read codes the same way.
 *
 * @param {unknown} status the envelope's `status` field
 * @returns {number | null}
 */
export function parseEnvelopeCode(status) {
  const code =
    typeof status === "string" && /^\d+$/.test(status) ? Number(status) : status;
  return Number.isInteger(code) ? code : null;
}

// Every HTTP-date form (IMF-fixdate, RFC 850, asctime) carries hh:mm:ss.
// Requiring it keeps V8's lenient Date.parse from accepting junk like "-5".
const HTTP_DATE_TIME = /\b\d{2}:\d{2}:\d{2}\b/;
const HAS_ZONE = /\b(GMT|UTC)\b|[+-]\d{4}\b/;

/**
 * Parses a `Retry-After` header value into whole seconds from `nowMs`.
 *
 * Accepts delay-seconds (`"20"`) or an HTTP-date. A date in the past gives 0.
 *
 * @param {string | null | undefined} value
 * @param {number} [nowMs] Defaults to `Date.now()`.
 * @returns {number | null} null when missing or invalid.
 */
export function parseRetryAfter(value, nowMs = Date.now()) {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text) return null;

  if (/^\d+$/.test(text)) return Number.parseInt(text, 10);

  if (!HTTP_DATE_TIME.test(text)) return null;
  // asctime has no zone, but HTTP-dates are always GMT.
  const at = Date.parse(HAS_ZONE.test(text) ? text : `${text} GMT`);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.ceil((at - nowMs) / 1000));
}
